import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '../layout/PageHeader';
import {
  Container,
  Typography,
  Button,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { CreditsSection } from '../subscription/CreditsSection';
import { MySolicitations } from '../solicitations/MySolicitations';

const drawerWidth = 240;

export const Dashboard: React.FC = () => {
  const menuItems = useMemo(() => [
    { text: 'My Solicitations', icon: <DashboardIcon />, id: 'dashboard' },
    { text: 'Credits', icon: <CreditCardIcon />, id: 'credits' },
    { text: 'Settings', icon: <SettingsIcon />, id: 'settings' },
  ], []);

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('dashboard');

  // Handle initial tab selection from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && menuItems.some(item => item.id === tab)) {
      setSelectedTab(tab);
    }
  }, [location, menuItems]);

  // Handle drawer state based on screen size
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleTabChange = (tabId: string) => {
    setSelectedTab(tabId);
    // Update URL without reloading the page
    const newUrl = `${location.pathname}?tab=${tabId}`;
    window.history.pushState({}, '', newUrl);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'credits':
        return (
          <>
            <PageHeader
              title="Credit Balance"
              description="Manage your credits for AI-powered document analysis"
            />
            <CreditsSection />
          </>
        );
      case 'settings':
        return (
          <>
            <PageHeader
              title="Settings"
              description="Manage your account preferences and settings"
            />
            <Box sx={{ mt: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Coming soon...
              </Typography>
            </Box>
          </>
        );
      default:
        return (
          <>
            <PageHeader
              title="My Solicitations"
              description={`Track and manage your saved opportunities`}
            />
            <MySolicitations />
          </>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            GovToolsPro
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={selectedTab === item.id}
                  onClick={() => handleTabChange(item.id)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: drawerOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Container maxWidth="lg">
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};
